import { useState } from "react";
import { Box, Tooltip, Typography, ButtonGroup, Button } from "@mui/material";
import { map } from "lodash";
import TabelaBase, { CelulaTexto } from "./tabelaBase";
import Usuario from "../../../models/usuario";
import {
  IconPencil,
  IconRestore,
  IconSend,
  IconShieldCheck,
  IconUserOff,
} from "@tabler/icons-react";
import { Perfil } from "../../../models/enum";
import PermissaoDisplay from "../permissaoDisplay";
import ExcluirClienteDialog from "../../app/conta/business/excluirClienteDialog";
import EditarClienteDialog from "../../app/conta/business/editarClienteDialog";
import ConvidarClienteDialog from "../../app/conta/business/convidarClienteDialog";
import DesarquivarClienteDialog from "../../app/conta/business/desarquivarClienteDialog";
import FelixAvatar from "../ui/felixAvatar";
import { useNavigate } from "react-router-dom";

type Props = {
  clientes: Usuario[];
};

export default function TabelaClientes({ clientes }: Props) {
  const header: {
    titulo: string;
    key: string;
    ordem: number;
    align: "left" | "center" | "right";
  }[] = [
    { titulo: "Nome", key: "nome", ordem: 1, align: "left" },
    { titulo: "E-mail", key: "email", ordem: 2, align: "left" },
    { titulo: "Atualizado em", key: "atualizadoEm", ordem: 3, align: "right" },
    { titulo: "", key: "acoes", ordem: 4, align: "right" },
  ];

  const rows = map(clientes, (cliente) => ({
    nome: celulaNome(cliente),
    email: celulaEmail(cliente),
    atualizadoEm: celulaAtualizadoEm(cliente),
    acoes: celulaAcoes(cliente),
  }));

  return <TabelaBase header={header} rows={rows} />;

  function celulaNome(cliente: Usuario): JSX.Element {
    return (
      <Tooltip title={cliente.id}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <FelixAvatar
            nome={cliente.nome}
            size={40}
            perfil={Perfil.INVESTIDOR}
            fotoDePerfil={cliente.fotoDePerfil}
          />
          <Box mr={1} />
          <Typography fontWeight={500}>{cliente.nome}</Typography>
        </Box>
      </Tooltip>
    );
  }

  function celulaEmail(cliente: Usuario): JSX.Element {
    return <CelulaTexto texto={cliente.email} />;
  }

  function celulaAtualizadoEm(cliente: Usuario): JSX.Element {
    if (!!cliente.atualizadoEm)
      return (
        <Typography variant="dataMono">
          {cliente.atualizadoEm.clone().format("DD/MM/YYYY")}
        </Typography>
      );
    return <Typography color="text.secondary">Nunca</Typography>;
  }

  function celulaAcoes(cliente: Usuario): JSX.Element {
    return (
      <PermissaoDisplay
        match={(claims) => !!claims?.permissoes?.gerenciar_clientes}
      >
        <ButtonGroup size="small" variant="outlined">
          <EditarCliente cliente={cliente} />
          {!!cliente.email && <PermissoesCliente cliente={cliente} />}
          {!cliente.arquivado && !!!cliente.email && (
            <ConvidarCliente cliente={cliente} />
          )}
          {!!!cliente.arquivado ? (
            <ExcluirCliente cliente={cliente} />
          ) : (
            <DesarquivarCliente cliente={cliente} />
          )}
        </ButtonGroup>
      </PermissaoDisplay>
    );
  }
}

function EditarCliente({ cliente }: { cliente: Usuario }) {
  const [openEditar, setOpenEditar] = useState(false);

  return (
    <>
      {openEditar && (
        <EditarClienteDialog
          open={openEditar}
          cliente={cliente}
          onClose={() => setOpenEditar(false)}
        />
      )}
      <Tooltip title="Editar cliente">
        <Button color="info" size="small" onClick={() => setOpenEditar(true)}>
          <IconPencil size={16} />
        </Button>
      </Tooltip>
    </>
  );
}

function PermissoesCliente({ cliente }: { cliente: Usuario }) {
  const navigate = useNavigate();
  return (
    <Tooltip title="Permissões">
      <Button
        color="info"
        size="small"
        onClick={() =>
          navigate("/cliente", {
            state: { usuarioId: cliente.id },
          })
        }
      >
        <IconShieldCheck size={16} />
      </Button>
    </Tooltip>
  );
}

function ConvidarCliente({ cliente }: { cliente: Usuario }) {
  const [open, setOpen] = useState(false);

  if (!!cliente.arquivado) return <></>;

  return (
    <>
      {open && (
        <ConvidarClienteDialog
          open={open}
          cliente={cliente}
          onClose={() => setOpen(false)}
        />
      )}
      <Tooltip title="Convidar cliente">
        <Button size="small" color="info" onClick={() => setOpen(true)}>
          <IconSend size={16} />
        </Button>
      </Tooltip>
    </>
  );
}

function DesarquivarCliente({ cliente }: { cliente: Usuario }) {
  const [openDesarquivar, setOpenDesarquivar] = useState(false);

  return (
    <>
      <DesarquivarClienteDialog
        open={openDesarquivar}
        cliente={cliente}
        onClose={() => setOpenDesarquivar(false)}
      />
      <Tooltip title="Restaurar cliente">
        <Button
          size="small"
          color="success"
          onClick={() => setOpenDesarquivar(true)}
        >
          <IconRestore size={16} />
        </Button>
      </Tooltip>
    </>
  );
}

function ExcluirCliente({ cliente }: { cliente: Usuario }) {
  const [openExcluir, setOpenExcluir] = useState(false);

  return (
    <>
      <ExcluirClienteDialog
        open={openExcluir}
        cliente={cliente}
        onClose={() => setOpenExcluir(false)}
      />
      <Tooltip title={!!cliente.email ? "Remover cliente" : "Arquivar cliente"}>
        <Button size="small" color="error" onClick={() => setOpenExcluir(true)}>
          <IconUserOff size={16} />
        </Button>
      </Tooltip>
    </>
  );
}
